/**
 * Clinicians Page
 *
 * Page for managing organization members and invitations.
 * Part of the "Your Practice" feature.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { JotRxSetupCallout } from '@features/eprescriptions';
import { GUIDE_SURFACES, GuideLink, PageGuideLink } from '@features/knowledge-base/links';
import { useCan, useOrganizationInfo } from '@features/user';
import { Refresh as RefreshIcon, Tune as TuneIcon } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { EditPlansModal } from './components/EditPlansModal';
import { InviteUserDialog } from './components/InviteUserDialog';
import { MemberTable } from './components/MemberTable';
import { PendingInvitationsSection } from './components/PendingInvitationsSection';
import { RolePermissionsTable } from './components/RolePermissionsTable';
import {
  useGetAvailableRolesQuery,
  useGetInvitationsQuery,
  useGetOrganizationMembersQuery,
  useOrganizationAccess,
  useTransformInvitations,
  useTransformMembers,
} from './store';

export default function CliniciansPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();

  // State
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editPlansOpen, setEditPlansOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [autoEditUserId, setAutoEditUserId] = useState<string | null>(null);

  // Organization access
  const { userState, userIsInGroup, userIsBillingAdmin, organizationData, refetchOrg } =
    useOrganizationAccess();
  const isPracticeAdmin = useCan('manage_members');

  // Solo orgs don't have a Members surface — the Clinicians tab + nav link are
  // hidden for them. Guard the route too so a direct URL (or a stale link) for
  // a solo org redirects to the subscription page instead of rendering an
  // empty members view. `organizationInfo` is undefined until user-info loads,
  // so we only redirect once we positively know the org is not a group.
  const organizationInfo = useOrganizationInfo();
  const isSoloOrg = organizationInfo != null && !organizationInfo.group;

  // API Queries
  const {
    data: membersData,
    isLoading: membersLoading,
    refetch: refetchMembers,
  } = useGetOrganizationMembersQuery(
    {},
    {
      skip: !userIsInGroup,
    },
  );

  const { data: invitationsData, refetch: refetchInvitations } = useGetInvitationsQuery(undefined, {
    skip: !userIsInGroup || !userIsBillingAdmin,
  });

  const { data: rolesData } = useGetAvailableRolesQuery(undefined, {
    skip: !userIsInGroup,
  });

  // Transform data
  const { activeMembers, inactiveMembers } = useTransformMembers(membersData?.members);
  const invitations = useTransformInvitations(invitationsData?.invitations);

  // Track page view on mount
  useEffect(() => {
    if (membersData && invitationsData) {
      clinicians.pageViewed({
        member_count: membersData.members?.length || 0,
        active_member_count: activeMembers.length,
        pending_invitation_count: invitations.length,
      });
    }
  }, [membersData, invitationsData, activeMembers.length, invitations.length, clinicians]);

  // Callbacks
  const handleRefreshAll = useCallback(async () => {
    clinicians.refreshClicked();
    try {
      await Promise.all([refetchOrg(), refetchMembers(), refetchInvitations()]);
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    }
  }, [refetchOrg, refetchMembers, refetchInvitations, enqueueSnackbar]);

  const toggleRowExpanded = useCallback(
    (userId: string) => {
      setExpandedRows(prev => {
        const next = new Set(prev);
        const isExpanding = !next.has(userId);
        if (isExpanding) {
          next.add(userId);
          // Find member to get metadata
          const member = [...activeMembers, ...inactiveMembers].find(m => m.userId === userId);
          if (member) {
            clinicians.memberRowExpanded({
              member_id: userId,
              role_count: member.roles.length,
              has_bundles: (member.bundles?.length || 0) > 0,
            });
          }
        } else {
          next.delete(userId);
        }
        return next;
      });
    },
    [activeMembers, inactiveMembers, clinicians],
  );

  const handleInviteSuccess = useCallback(() => {
    setInviteDialogOpen(false);
    refetchInvitations();
    // Note: inviteSent event is tracked in InviteUserDialog component
  }, [refetchInvitations]);

  const handleMemberChange = useCallback(() => {
    refetchMembers();
  }, [refetchMembers]);

  const handleEditCurrentUser = useCallback(() => {
    if (userState?.user_id) {
      setAutoEditUserId(userState.user_id);
    }
  }, [userState?.user_id]);

  const handleAutoEditHandled = useCallback(() => {
    setAutoEditUserId(null);
  }, []);

  // Redirect solo orgs to the subscription page (which hosts their combined
  // single-page experience). Placed after all hooks so hook order stays stable.
  if (isSoloOrg) {
    return <Navigate to="/practice/subscriptions" replace />;
  }

  return (
    <>
      <Box component="header" sx={{ display: 'flex', '& > button': { mb: 3 } }}>
        <PageGuideLink page="members" />
      </Box>

      {/* JotRx Setup Callout */}
      <JotRxSetupCallout page="clinicians" onAction={handleEditCurrentUser} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => {
            clinicians.inviteDialogOpened();
            setInviteDialogOpen(true);
          }}
        >
          Invite User
        </Button>
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={() => setEditPlansOpen(true)}
          // Only meaningful when there are clinicians to edit.
          disabled={activeMembers.filter(m => m.roles.includes('clinician')).length === 0}
        >
          Edit Plans
        </Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshAll}>
          Refresh
        </Button>
      </Box>

      <Box id="member-table" sx={{ display: 'flex', flexDirection: 'column', gap: 4, mb: 6 }}>
        {/* Active Members Table */}
        <MemberTable
          title="Active Members"
          members={activeMembers}
          expandedRows={expandedRows}
          onToggleExpand={toggleRowExpanded}
          onMemberChange={handleMemberChange}
          availableRoles={rolesData?.roles}
          autoEditUserId={autoEditUserId}
          onAutoEditHandled={handleAutoEditHandled}
        />

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <PendingInvitationsSection
            invitations={invitations}
            onInvitationChange={refetchInvitations}
          />
        )}

        {/* Inactive Members Section */}
        {inactiveMembers.length > 0 && (
          <MemberTable
            title={`Inactive Members (${inactiveMembers.length})`}
            members={inactiveMembers}
            expandedRows={expandedRows}
            onToggleExpand={toggleRowExpanded}
            onMemberChange={handleMemberChange}
            availableRoles={rolesData?.roles}
            isInactive
            collapsible
            defaultCollapsed
          />
        )}

        {/* Permissions Matrix */}
        {isPracticeAdmin && (
          <RolePermissionsTable
            title="Role Permissions"
            titleControl={
              <GuideLink
                surfaceId={GUIDE_SURFACES.MEMBERS_ROLES_REFERENCE}
                shape="section-control"
                slug="access/roles-permissions"
              />
            }
          />
        )}
      </Box>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        availableRoles={rolesData?.roles}
        isTrialing={organizationData?.subscription_status === 'trialing'}
      />

      {/* Edit Plans (batch matrix) modal */}
      <EditPlansModal
        open={editPlansOpen}
        onClose={() => setEditPlansOpen(false)}
        onSuccess={() => {
          setEditPlansOpen(false);
          handleMemberChange();
        }}
        members={activeMembers}
      />
    </>
  );
}
